// MarrsingClause® Clarity Quiz — questions, profiles & scoring
// (Questions provided verbatim by the brand owner)

export const QUESTIONS = [
  {
    q: "When you think about what you want from your relationship, how clearly can you articulate it?",
    pillar: "Clarity",
    opts: [
      "Very clearly — I know exactly what I need",
      "Somewhat clearly, but I second-guess myself",
      "I find it hard to put into specific words",
      "I tend to go with the flow and see what happens",
    ],
  },
  {
    q: "How well do you understand your own relationship patterns — the habits and reactions you repeat?",
    pillar: "Clarity",
    opts: [
      "Very well — I've reflected on this deeply",
      "I'm aware of some patterns but not all",
      "I know something's there but haven't explored it",
      "I don't tend to think about this",
    ],
  },
  {
    q: "When you have something difficult to say to your partner, you typically:",
    pillar: "Communication",
    opts: [
      "Say it directly and work through it together",
      "Wait for the right moment — which rarely arrives",
      "Hint at it and hope they understand",
      "Avoid it entirely to keep the peace",
    ],
  },
  {
    q: "How often do you and your partner have conversations about what you each actually need — not just logistics?",
    pillar: "Communication",
    opts: [
      "Regularly — it's a natural part of how we connect",
      "Sometimes, but usually only when something breaks down",
      "Rarely — we assume the other person knows",
      "Almost never — it hasn't come up",
    ],
  },
  {
    q: "How aligned do you feel with your partner's vision for your future together?",
    pillar: "Alignment",
    opts: [
      "Very aligned — we've talked about it in depth",
      "Mostly aligned, but there are gaps I'm aware of",
      "Somewhat unsure — we haven't really discussed it",
      "I sense we might want different things",
    ],
  },
  {
    q: "When you and your partner disagree, how does it usually resolve?",
    pillar: "Alignment",
    opts: [
      "We work through it and both feel heard",
      "One of us backs down, but it lingers",
      "It gets avoided until it comes up again",
      "It rarely gets fully resolved",
    ],
  },
  {
    q: "How intentional were you about entering this relationship — did you define what you wanted before committing?",
    pillar: "Clarity",
    opts: [
      "Very intentional — I knew my values and expressed them",
      "Mostly intentional, with some assumptions",
      "I hoped for the best but didn't define much",
      "I didn't think about it — I just followed my feelings",
    ],
  },
  {
    q: "If your relationship had a structure — a shared framework you both consciously practiced — how would that feel?",
    pillar: "Alignment",
    opts: [
      "Exciting — that's exactly what I've been looking for",
      "Interesting — I'd want to understand it better",
      "Unusual — relationships don't usually work that way",
      "Unnecessary — love should be enough",
    ],
  },
];

export const PROFILES = {
  clarity: {
    key: "clarity",
    name: "Clarity Seeker",
    title: "The foundation is ready to be built.",
    body:
      "You have love, connection, and good intentions — and the most meaningful work ahead of you is inward. Understanding your own values, needs, and patterns at a deeper level will transform how you show up for your partner. The Clarity pillar is your starting point.",
    teaser:
      "with strong relational instincts and the most growth opportunity in knowing yourself more specifically.",
    accent: "#4AAED8",
    scores: { Clarity: 35, Communication: 60, Alignment: 55 },
    icon: "🪞",
  },
  communication: {
    key: "communication",
    name: "Communication Builder",
    title: "You know yourself. Now say it.",
    body:
      "Your self-awareness is a genuine strength — you understand what you want and who you are in this relationship. The gap is in translating that self-knowledge into honest, consistent dialogue with your partner. The Communication pillar is where you'll find your greatest leverage.",
    teaser:
      "strong in self-knowledge, with the most growth opportunity in expressing what matters most.",
    accent: "#7FCDBB",
    scores: { Clarity: 70, Communication: 38, Alignment: 58 },
    icon: "💬",
  },
  alignment: {
    key: "alignment",
    name: "Alignment Finder",
    title: "Clear individually. Building together.",
    body:
      "You and your partner each have strong individual self-knowledge and communicate reasonably well — the work now is ensuring that your individual visions are consciously being built into a shared one. Alignment asks: are we moving in the same direction, with the same intention? That conversation is ready to be had.",
    teaser:
      "clear on who you are individually, with the most opportunity in building a conscious shared direction.",
    accent: "#D7B56D",
    scores: { Clarity: 72, Communication: 68, Alignment: 34 },
    icon: "🌿",
  },
};

export function computeProfile(answers) {
  let cScore = 0, comScore = 0, alScore = 0;
  let cN = 0, comN = 0, alN = 0;
  QUESTIONS.forEach((q, i) => {
    const ans = answers[i] ?? 0;
    const score = (3 - ans) / 3; // 0..1 (better = higher)
    if (q.pillar === "Clarity") { cScore += score; cN++; }
    else if (q.pillar === "Communication") { comScore += score; comN++; }
    else { alScore += score; alN++; }
  });
  const c = cScore / cN;
  const com = comScore / comN;
  const al = alScore / alN;

  // The lowest pillar is the user's growth area (their profile)
  const min = Math.min(c, com, al);
  let key = "alignment";
  if (c === min) key = "clarity";
  else if (com === min) key = "communication";

  const scores = {
    Clarity: Math.round(c * 100),
    Communication: Math.round(com * 100),
    Alignment: Math.round(al * 100),
  };
  return { key, scores };
}
